(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!customElements.get('safir-premium-gallery')) {
    customElements.define('safir-premium-gallery', class extends HTMLElement {
      connectedCallback() {
        this.rail = this.querySelector('[data-gallery-rail]');
        this.items = [...this.querySelectorAll('[data-media-id].safir-premium-gallery__item')];
        this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
        const galleryData = this.querySelector('[data-variant-galleries]');
        try {
          this.variantGalleries = galleryData ? JSON.parse(galleryData.textContent) : {};
        } catch (error) {
          this.variantGalleries = {};
        }
        this.isPaused = false;
        this.isDragging = false;
        this.lastTime = 0;
        if (!this.rail) return;

        this.rail.querySelectorAll('img').forEach((image) => image.setAttribute('draggable', 'false'));
        this.rail.addEventListener('dragstart', (event) => event.preventDefault());
        this.rail.addEventListener('pointerdown', (event) => this.startDrag(event));
        this.rail.addEventListener('pointermove', (event) => this.drag(event));
        this.rail.addEventListener('pointerup', (event) => this.endDrag(event));
        this.rail.addEventListener('pointercancel', (event) => this.endDrag(event));
        this.rail.addEventListener('touchstart', () => this.pauseDrift(), { passive: true });
        this.rail.addEventListener('touchend', () => this.resumeDriftLater(), { passive: true });
        this.rail.addEventListener('touchcancel', () => this.resumeDriftLater(), { passive: true });
        this.rail.addEventListener('wheel', () => this.pauseDrift(2000), { passive: true });
        this.rail.addEventListener('keydown', () => this.pauseDrift(2000));
        this.rail.addEventListener('scroll', () => this.queueActiveUpdate(), { passive: true });
        this.dots.forEach((dot) => dot.addEventListener('click', () => {
          this.pauseDrift(2000);
          this.showMedia(dot.dataset.mediaId, true);
        }));

        const productRoot = this.closest('safir-premium-product');
        productRoot?.addEventListener('safir:variant-change', (event) => {
          this.filterByVariant(event.detail.id, event.detail.featuredMediaId);
          if (event.detail.featuredMediaId) this.showMedia(String(event.detail.featuredMediaId), true);
        });
        this.filterByVariant(galleryData?.dataset.selectedVariantId, this.dataset.selectedMedia);

        if (this.dataset.autodrift === 'true' && !reduceMotion.matches) {
          this.observer = new IntersectionObserver((entries) => {
            this.inView = entries[0].isIntersecting;
            if (!this.inView) {
              cancelAnimationFrame(this.frame);
              this.frame = null;
              this.lastTime = 0;
              this.classList.remove('is-auto-drifting');
              return;
            }
            if (!this.isPaused) this.startDrift();
          }, { threshold: 0.25 });
          this.observer.observe(this);
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        cancelAnimationFrame(this.frame);
        window.clearTimeout(this.resumeTimer);
        window.clearTimeout(this.activeTimer);
      }

      drift(time) {
        if (this.isPaused || !this.inView || reduceMotion.matches) {
          this.frame = null;
          this.lastTime = 0;
          this.classList.remove('is-auto-drifting');
          return;
        }
        if (!this.lastTime) this.lastTime = time;
        const delta = Math.min(time - this.lastTime, 32);
        this.lastTime = time;
        this.rail.scrollLeft += delta * 0.05;
        this.loopGallery();
        this.frame = requestAnimationFrame((nextTime) => this.drift(nextTime));
      }

      loopGallery() {
        const firstItem = [...this.rail.children].find((item) => item.matches('.safir-premium-gallery__item:not([hidden])'));
        if (!firstItem) return;
        const styles = window.getComputedStyle(this.rail);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
        const step = firstItem.getBoundingClientRect().width + gap;
        if (step <= 0 || this.rail.scrollLeft < step) return;
        this.rail.append(firstItem);
        this.rail.scrollLeft -= step;
        this.items = [...this.querySelectorAll('[data-media-id].safir-premium-gallery__item')];
      }

      startDrift() {
        if (this.frame || this.isPaused || !this.inView || reduceMotion.matches) return;
        this.lastTime = 0;
        this.classList.add('is-auto-drifting');
        this.frame = requestAnimationFrame((time) => this.drift(time));
      }

      pauseDrift(duration) {
        this.isPaused = true;
        this.classList.remove('is-auto-drifting');
        cancelAnimationFrame(this.frame);
        this.frame = null;
        window.clearTimeout(this.resumeTimer);
        if (duration) this.resumeDriftLater(duration);
      }

      resumeDriftLater(duration = 2000) {
        window.clearTimeout(this.resumeTimer);
        this.resumeTimer = window.setTimeout(() => {
          this.isPaused = false;
          this.startDrift();
        }, duration);
      }

      startDrag(event) {
        if (event.pointerType === 'touch') return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.pauseDrift();
        if (event.pointerType === 'mouse') event.preventDefault();
        this.isDragging = true;
        this.dragCommitted = event.pointerType === 'mouse';
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.dragStartScroll = this.rail.scrollLeft;
        this.rail.setPointerCapture(event.pointerId);
        if (this.dragCommitted) this.classList.add('is-dragging');
      }

      drag(event) {
        if (!this.isDragging) return;
        const distanceX = event.clientX - this.dragStartX;
        const distanceY = event.clientY - this.dragStartY;
        if (!this.dragCommitted) {
          if (Math.abs(distanceX) < 6) return;
          if (Math.abs(distanceX) <= Math.abs(distanceY)) return;
          this.dragCommitted = true;
          this.classList.add('is-dragging');
        }
        event.preventDefault();
        this.rail.scrollLeft = this.dragStartScroll - distanceX;
      }

      endDrag(event) {
        if (event.pointerType === 'touch') return;
        if (this.isDragging && this.rail.hasPointerCapture(event.pointerId)) this.rail.releasePointerCapture(event.pointerId);
        this.isDragging = false;
        this.classList.remove('is-dragging');
        this.resumeDriftLater();
      }

      filterByVariant(variantId, preferredMediaId) {
        const allowed = (this.variantGalleries[String(variantId)] || []).map(String);
        const filtering = allowed.length > 0;
        this.items.forEach((item) => {
          item.hidden = filtering && !allowed.includes(String(item.dataset.mediaId));
        });
        this.dots.forEach((dot) => {
          dot.hidden = filtering && !allowed.includes(String(dot.dataset.mediaId));
        });
        const visibleItems = this.items.filter((item) => !item.hidden);
        const selected = visibleItems.find((item) => String(item.dataset.mediaId) === String(preferredMediaId)) || visibleItems[0];
        if (selected) this.setActive(selected);
      }

      showMedia(mediaId, smooth) {
        const item = this.items.find((candidate) => candidate.dataset.mediaId === mediaId);
        if (!item) return;
        this.classList.add('is-transitioning');
        item.scrollIntoView({ behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto', block: 'nearest', inline: 'start' });
        window.setTimeout(() => this.classList.remove('is-transitioning'), reduceMotion.matches ? 0 : 420);
        this.setActive(item);
      }

      queueActiveUpdate() {
        if (this.activeTimer) return;
        this.activeTimer = window.setTimeout(() => {
          const left = this.rail.getBoundingClientRect().left;
          const item = this.items.reduce((closest, candidate) => {
            return Math.abs(candidate.getBoundingClientRect().left - left) < Math.abs(closest.getBoundingClientRect().left - left) ? candidate : closest;
          }, this.items[0]);
          if (item) this.setActive(item);
          this.activeTimer = null;
        }, 180);
      }

      setActive(item) {
        this.items.forEach((candidate) => candidate.classList.toggle('is-selected', candidate === item));
        this.dots.forEach((dot) => dot.classList.toggle('is-active', dot.dataset.mediaId === item.dataset.mediaId));
      }
    });
  }

  if (!customElements.get('safir-premium-product')) {
    customElements.define('safir-premium-product', class extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('[data-premium-variants]');
        if (!data) return;
        this.variants = JSON.parse(data.textContent);
        this.optionGroups = [...this.querySelectorAll('[data-option-position]')];
        this.variantInput = this.querySelector('[data-premium-variant-id]');
        this.addButton = this.querySelector('[name="add"]');
        this.optionGroups.forEach((group) => group.addEventListener('change', () => this.updateVariant()));
      }

      updateVariant() {
        const selectedOptions = this.optionGroups.map((group) => group.querySelector('input:checked')?.value);
        const variant = this.variants.find((candidate) => candidate.options.every((option, index) => option === selectedOptions[index]));
        this.optionGroups.forEach((group) => {
          const selected = group.querySelector('input:checked');
          const label = group.querySelector('[data-selected-option]');
          if (selected && label) label.textContent = selected.value;
        });
        if (!variant) {
          this.setAvailability(false, 'Kullanılamıyor');
          return;
        }

        this.variantInput.value = variant.id;
        this.querySelector('[data-current-price]').textContent = variant.price;
        const compare = this.querySelector('[data-compare-price]');
        compare.textContent = variant.comparePrice;
        compare.hidden = !variant.compareAtPrice || variant.compareAtPrice <= variant.priceValue;
        this.setAvailability(variant.available, variant.available ? 'Sepete ekle' : 'Tükendi');
        this.dispatchEvent(new CustomEvent('safir:variant-change', { bubbles: false, detail: variant }));
        window.dispatchEvent(new CustomEvent('safir:variant-change', { detail: variant }));

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      setAvailability(available, label) {
        this.addButton.disabled = !available;
        this.addButton.querySelector('[data-add-label]').textContent = label;
      }
    });
  }

  if (!customElements.get('safir-scroll-story')) {
    customElements.define('safir-scroll-story', class extends HTMLElement {
      connectedCallback() {
        this.scenes = [...this.querySelectorAll('[data-story-scene]')];
        this.media = [...this.querySelectorAll('[data-story-media]')];
        this.sceneCount = this.scenes.length;
        if (!this.sceneCount) return;
        this.style.setProperty('--scene-count', this.sceneCount);
        this.dataset.ready = 'true';
        if (reduceMotion.matches) {
          this.dataset.motion = 'reduced';
          return;
        }
        this.onScroll = () => {
          if (!this.frame) this.frame = requestAnimationFrame(() => this.update());
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        cancelAnimationFrame(this.frame);
      }

      update() {
        const rect = this.getBoundingClientRect();
        const distance = Math.max(rect.height - window.innerHeight, 1);
        const progress = Math.min(1, Math.max(0, -rect.top / distance));
        const active = Math.min(this.sceneCount - 1, Math.floor(progress * this.sceneCount));
        this.style.setProperty('--story-progress', progress.toFixed(4));
        this.scenes.forEach((scene, index) => scene.classList.toggle('is-active', index === active));
        this.media.forEach((media, index) => media.classList.toggle('is-active', index === active));
        this.dataset.activeScene = active;
        this.frame = null;
      }
    });
  }
})();
